import {
  DeleteOutlined,
  EditOutlined,
  FilterOutlined,
  PlusOutlined,
  PrinterOutlined,
  SyncOutlined,
} from '@ant-design/icons';
import {
  Alert,
  Badge,
  Button,
  Card,
  Form,
  Input,
  Modal,
  Pagination,
  Popconfirm,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import PropTypes from 'prop-types';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import toast from 'react-hot-toast';
import {BsFiletypeRaw} from 'react-icons/bs';
import {CgDanger} from 'react-icons/cg';
import {FaCheck} from 'react-icons/fa';
import {FaSackDollar} from 'react-icons/fa6';
import {useSelector} from 'react-redux';
import {useLocation, useNavigate} from 'react-router-dom';

import {StatCard} from '../../../../CommonComponents/Cards';
import TableData from '../../../../CommonComponents/TableData/TableData.js';
import {
  RQExcelUpload,
  RQGetCategoryAPI,
  RQGetProductsByFilter,
  RqDeleteProduct,
  RqGetAllProducts,
  RqGetProducts,
  RqUpdateProduct,
} from '../../../../reactQuery/apis';
import {selectAuth} from '../../../../redux/selectors';
import BarcodeGenerate from '../../GenerateBarcode/BarcodeGenerate.js';

import {AddProductScreen} from './AddProductScreen.js';
import EditProductScreen from './EditProductScreen/EditProductScreen';
import ProductDetailScreen from './ProductDetailScreen/ProductDetailScreen.js';

const {Text, Title} = Typography;
const {Option} = Select;

const STATUS_OPTIONS = [
  {label: 'Active', value: 'ACTIVE'},
  {label: 'Inactive', value: 'INACTIVE'},
];

const mapImagesToFileList = (images = []) =>
  images.map((url, index) => ({
    uid:    `img-${index}`,
    name:   url.split('/').pop(),
    status: 'done',
    url,
  }));

const hasActiveFilters = filters =>
  !!(
    filters?.minPrice     ||
    filters?.category     ||
    filters?.brand        ||
    filters?.productType  ||
    filters?.productName  ||
    filters?.features?.length
  );

const toRow = (product, index) => ({
  key:         product.id,
  index:       index + 1,
  id:          product.id,
  productName: product.productName,
  eanCode:     product.eanCode || product.barcode || '',
  barcode:     product.barcode || '',
  status:      product.status,
  tax:         product.tax || (product.cgst != null || product.sgst != null || product.igst != null ? {cgst: product.cgst, sgst: product.sgst, igst: product.igst} : null),
  quantity:    product.quantity ?? 0,
  createdOn:   product.createdOn,
});

async function uploadVariantImage({file, productId, color, token}) {
  try {
    const res = await fetch('/api/uploads/presigned', {
      method:  'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization:  `Bearer ${token}`,
      },
      body: JSON.stringify({
        productId,
        variantColor: color,
        fileName:     file.name,
        contentType:  file.type,
      }),
    });
    if (!res.ok) {throw new Error('Failed to get presigned URL');}
    const {uploadUrl, fileUrl} = await res.json();
    const uploadRes = await fetch(uploadUrl, {
      method:  'PUT',
      headers: {'Content-Type': file.type},
      body:    file,
    });
    if (!uploadRes.ok) {throw new Error('Failed to upload image');}
    return fileUrl;
  } catch (err) {
    console.error('Upload Error:', err);
    return null;
  }
}

const ProductScreen = () => {
  const auth     = useSelector(selectAuth);
  const location = useLocation();
  const navigate = useNavigate();
  const token    = auth?.tokenData;

  // ── Forms ──
  const [form]    = Form.useForm();
  const [formTwo] = Form.useForm();

  const [isModalVisible,     setIsModalVisible]     = useState(false);
  const [editModal,          setEditModal]          = useState({visible: false, record: null});
  const [viewModal,          setViewModal]          = useState({visible: false, record: null});
  const [barcodeModal,       setBarcodeModal]       = useState({visible: false, record: null});
  const [variantModal,       setVariantModal]       = useState({visible: false, productData: null});

  // ── Data state ──
  const [dataSource,  setDataSource]  = useState([]);
  const [imageFiles,  setImageFiles]  = useState([]);
  const [previewUrls, setPreviewUrls] = useState([]);

  // ── Filter / pagination state ──
  const [filters,        setFilters]        = useState({});
  const [searchText,     setSearchText]     = useState('');
  const [categoryFilter, setCategoryFilter] = useState(null);
  const [statusFilter,   setStatusFilter]   = useState(null);

  const openEditHandled = useRef(false);
  const fileInputRef    = useRef(null);

  // ── API queries ──
  const {data: userData,            isLoading, error} = RqGetAllProducts();
  const {data: categories}                            = RQGetCategoryAPI(token);
  const {data: filteredProductsData}                  = RQGetProductsByFilter(filters);
  const {data: allProducts}                           = RqGetProducts();

  // ── Derived options ──
  const categoryOptions = useMemo(
    () =>
      categories
        ?.filter(c => c.isActive)
        .map(c => ({value: c.name, label: c.name, features: c.features})) ?? [],
    [categories],
  );

  // ── Mutations ──
  const updateProductMutation = RqUpdateProduct({
    onSuccess: () => {
      toast.success('Product updated successfully');
      setEditModal({visible: false, record: null});
    },
    onError: err => toast.error(err.message || 'Product update failed'),
  });

  const deleteProductMutation = RqDeleteProduct({
    onSuccess: () => {
      toast.success('Product deleted successfully');
      handleCancel();
    },
    onError: err => toast.error(err.message || 'Failed to delete product'),
  });

  const {mutate: uploadExcel} = RQExcelUpload({
    onSuccess: () => {
      toast.success('Products imported successfully');
      handleCancel();
    },
    onError: error => toast.error(error?.message || 'Failed to import Excel'),
  });

  // ── Handlers (defined before useEffects that reference them) ──
  const handleCancel = useCallback(() => {
    form.resetFields();
    setImageFiles([]);
    setPreviewUrls([]);
    setIsModalVisible(false);
  }, [form]);

  const openEditModal = useCallback(record => {
    setEditModal({visible: true, record});
    setImageFiles(mapImagesToFileList(record.variants?.[0]?.images || []));
    formTwo.setFieldsValue({
      ...record,
      promoCode: record.promoCode ?? record.promocodeInfo?.code ?? null,
      imageUrl:  mapImagesToFileList(record.imageUrl || []),
      variants:  (record.variants || []).map(v => ({
        ...v,
        images: mapImagesToFileList(v.images || []),
      })),
    });
  }, [formTwo]);

  useEffect(() => {
    const source = hasActiveFilters(filters) ? filteredProductsData : allProducts;
    if (!source) {return;}

    const formatted = source
      .filter(product => {
        if (product.status !== 'ACTIVE' && product.status !== 'INACTIVE') {return false;}
        if (filters.status?.length && !filters.status.includes(product.status)) {return false;}
        if (filters.features?.length) {
          const pf = product.features || [];
          if (!filters.features.some(f => pf.includes(f))) {return false;}
        }
        return true;
      })
     
      .sort((a, b) => new Date(b.createdOn ?? 0) - new Date(a.createdOn ?? 0))
      .map(toRow);

    setDataSource(formatted);
  }, [filters, filteredProductsData, allProducts]);

  // ── Auto-open edit modal from navigation state ──
  useEffect(() => {
    if (!location.state?.openEdit) {
      openEditHandled.current = false;
      return;
    }
    if (!openEditHandled.current && location.state?.productId && dataSource.length > 0) {
      const product = dataSource.find(p => p.id === location.state.productId);
      if (product) {
        openEditHandled.current = true;
        openEditModal(product);
        navigate(location.pathname, {replace: true});
      }
    }
  }, [dataSource, location.state, openEditModal, navigate]);

  // ── Stats ──
  const stats = useMemo(() => {
    const active     = dataSource.filter(p => p.status === 'ACTIVE').length;
    const lowStock   = dataSource.filter(p => (p.quantity ?? 0) < 10).length;
    const totalValue = dataSource.reduce((s, p) => s + (p.sellingPrice || 0), 0);
    return {total: dataSource.length, active, lowStock, totalValue};
  }, [dataSource]);

  const handleDelete = useCallback(async id => {
    try {
      await deleteProductMutation.mutateAsync({token, id});
    } catch (err) {
      console.error(err);
    }
  }, [deleteProductMutation, token]);

  const handleSearch = useCallback((value = '') => {
    const cleaned = value.trim().toLowerCase();
    setSearchText(value);
    setFilters(prev => {
      const next = {...prev};
      if (cleaned) {next.productName = cleaned;}
      else {delete next.productName;}
      return next;
    });
  }, []);

  const handleStatusChange = useCallback(value => {
    setStatusFilter(value ?? null);
    setFilters(prev => {
      const next = {...prev};
      if (value) {next.status = [value];}
      else {delete next.status;}
      return next;
    });
  }, []);

  const handleResetFilters = useCallback(() => {
    setCategoryFilter(null);
    setStatusFilter(null);
    setFilters({});
    setSearchText('');
  }, []);

  const handleExcelUpload = useCallback(e => {
    const file = e.target.files[0];
    if (!file) {return;}
    const formData = new FormData();
    formData.append('file', file);
    uploadExcel({token, formData});
  }, [token, uploadExcel]);

  const handleModalImageUpload = useCallback(async ({file, onSuccess, onError}, productId, color) => {
    try {
      const url = await uploadVariantImage({file, productId, color, token});
      if (!url) {throw new Error('Upload failed');}
      onSuccess({url});
      return url;
    } catch (err) {
      onError(err);
    }
  }, [token]);

  const handleUpdateSubmit = useCallback(async () => {
    try {
      const values      = await form.validateFields();
      const productData = {...values, imageUrl: [], promoCode: values.promoCode ?? null};
      updateProductMutation.mutate({token, id: editModal.record.id, productData});
    } catch (err) {
      console.error('Edit submit failed:', err);
    }
  }, [form, updateProductMutation, token, editModal.record]);

  // ── Table columns ──
  // Note: each column must have a unique `key` to avoid React warnings.
  const columns = useMemo(() => [
    {
      title:     <Text className={'text-xs font-bold text-stone-50 uppercase'}>Sr. No.</Text>,
      dataIndex: 'index',
      key:       'index',
      width:     60,
      render:    v => <Text className={'text-stone-500 font-medium'}>{v}</Text>,
    },
    {
      title: <Text className={'text-xs font-bold text-stone-50 uppercase'}>Product</Text>,
      key:   'product',
      render: (_, record) => (
        <div className={'font-medium text-base'}>{record.productName}</div>
      ),
    },
    {
      title: <Text className={'text-xs font-bold text-stone-50 uppercase'}>EAN / Barcode</Text>,
      key:   'codes',
      width: 160,
      render: (_, r) => (
        <Space orientation={'vertical'} size={2}>
          {r.eanCode && <Text className={'font-semibold'}>{r.eanCode}</Text>}
          {r.barcode && r.barcode !== r.eanCode && (
            <Text className={'font-mono text-gray-400'}>{r.barcode}</Text>
          )}
        </Space>
      ),
    },
    {
      title:     <Text className={'text-xs font-bold text-stone-50 uppercase'}>Status</Text>,
      dataIndex: 'status',
      key:       'status',
      width:     110,
      render: status => (
        <Badge
          status={status === 'ACTIVE' ? 'success' : 'error'}
          text={(
            <Tag
              color={status === 'ACTIVE' ? 'green' : 'red'}
              className={'rounded-full font-semibold'}
            >
              {status || 'INACTIVE'}
            </Tag>
          )}
        />
      ),
    },
    {
      title:     <Text className={'text-xs font-bold text-stone-50 uppercase'}>Qty</Text>,
      dataIndex: 'quantity',
      key:       'quantity',
      width:     80,
      sorter:    (a, b) => (a.quantity || 0) - (b.quantity || 0),
      render: v => {
        const qty = v ?? 0;
        return (
          <Text className={`font-bold ${qty < 10 ? 'text-red-500' : ''}`}>
            {qty}
          </Text>
        );
      },
    },
    {
      title:  <Text className={'text-xs font-bold text-stone-50 uppercase'}>CGST</Text>,
      key:    'cgst',   // ← was 'tax' on all three, causing duplicate-key warning
      width:  130,
      render: (_, r) =>
        r.tax ? (
          <Tag color={'blue'} className={'rounded-full text-base'}>{r.tax.cgst}%</Tag>
        ) : (
          <Text type={'secondary'}>—</Text>
        ),
    },
    {
      title:  <Text className={'text-xs font-bold text-stone-50 uppercase'}>SGST</Text>,
      key:    'sgst',
      width:  130,
      render: (_, r) =>
        r.tax ? (
          <Tag color={'blue'} className={'rounded-full text-base'}>{r.tax.sgst}%</Tag>
        ) : (
          <Text type={'secondary'}>—</Text>
        ),
    },
    {
      title:  <Text className={'text-xs font-bold text-stone-50 uppercase'}>IGST</Text>,
      key:    'igst',
      width:  130,
      render: (_, r) =>
        r.tax ? (
          <Tag color={'blue'} className={'rounded-full text-base'}>{r.tax.igst}%</Tag>
        ) : (
          <Text type={'secondary'}>—</Text>
        ),
    },
    {
      title: <Text className={'text-xs font-bold text-stone-50 uppercase'}>Actions</Text>,
      key:   'action',
      width: 120,
      render: (_, record) => (
        <div className={'flex gap-2 mx-2'}>
          <Tooltip title={'Edit'}>
            <EditOutlined
              className={'cursor-pointer border p-1 rounded-md text-blue-600'}
              onClick={e => { e.stopPropagation(); openEditModal(record); }}
            />
          </Tooltip>
          <Tooltip title={'Print Barcode'}>
            <PrinterOutlined
              className={'cursor-pointer border p-1 rounded-md text-green-600'}
              onClick={e => { e.stopPropagation(); setBarcodeModal({visible: true, record}); }}
            />
          </Tooltip>
          <Tooltip title={'Delete product'}>
            <Popconfirm
              title={'Are you sure you want to delete this product?'}
              onConfirm={() => handleDelete(record.id)}
              okText={'Yes'}
              cancelText={'No'}
            >
              <DeleteOutlined
                className={'cursor-pointer border p-1 rounded-md text-red-600'}
                onClick={e => e.stopPropagation()}
              />
            </Popconfirm>
          </Tooltip>
        </div>
      ),
    },
  ], [openEditModal, handleDelete]);

  // ── Guards ──
  if (!token)    {return null;}
  if (isLoading) {return <div className={'flex justify-center items-center h-64'}><Spin size={'large'} description={'Loading products…'} /></div>;}
  if (error)     {return <Alert message={'Failed to load products'} type={'error'} />;}

  // ── Render ──
  return (
    <div className={'min-h-screen md:p-5 p-1'}>

      {/* Header */}
      <div className={'flex items-center justify-between mb-6 flex-wrap gap-3'}>
        <div className={'flex items-center gap-3'}>
          <div
            className={'w-12 h-10 rounded-xl flex items-center justify-center text-white'}
            style={{background: 'linear-gradient(135deg, #E83F25, #A62C2C)'}}
          >
            <BsFiletypeRaw style={{fontSize: 22}} />
          </div>
          <Title level={3} className={'header !mb-0'}>Raw Products </Title>
        </div>

        <Space>
          <Button
            type={'primary'}
            icon={<PlusOutlined />}
            className={'btn-primary'}
            onClick={() => setIsModalVisible(true)}
          >
            Add Product
          </Button>
          
          <input
            ref={fileInputRef}
            type={'file'}
            accept={'.xlsx,.xls'}
            hidden
            onChange={handleExcelUpload}
          />
        </Space>
      </div>

      {/* Stats */}
      <div className={'grid grid-cols-2 md:grid-cols-4 gap-3 mb-4'}>
        <StatCard label={'Total Raw Products'} value={stats.total}                              icon={<BsFiletypeRaw className={'bg-color'} />} bg={'#e6e9ec'} />
        <StatCard label={'Active'}             value={stats.active}                             icon={<FaCheck       className={'bg-color'} />} bg={'#e6e9ec'} />
        <StatCard label={'Low Stock'}          value={stats.lowStock}                           icon={<CgDanger      className={'bg-color'} />} bg={'#e6e9ec'} />
        <StatCard label={'Catalogue Value'}    value={`₹${stats.totalValue.toLocaleString()}`}  icon={<FaSackDollar  className={'bg-color'} />} bg={'#e6e9ec'} />
      </div>

      {/* Filters */}
      <Card className={'rounded-lg border-0 shadow-sm overflow-hidden mb-2'}>
        <div className={'flex items-center justify-between mb-3 flex-wrap gap-2'}>
          <div className={'flex items-center gap-2'}>
            <FilterOutlined className={'text-indigo-500'} />
            <span className={'font-semibold text-gray-700 text-sm'}>Filters</span>
          </div>
          
        </div>

        <div className={'flex flex-wrap gap-3 items-center'}>
          <Input.Search
            value={searchText}
            onChange={e => handleSearch(e.target.value)}
            onSearch={handleSearch}
            placeholder={'Search products…'}
            allowClear
            className={'w-56'}
          />

          <Select
            placeholder={'Status'}
            allowClear
            value={statusFilter ?? undefined}
            onChange={handleStatusChange}
            options={STATUS_OPTIONS}
            className={'w-36'}
          />

          <Button
            icon={<SyncOutlined />}
            onClick={handleResetFilters}
            className={'rounded-md border-gray-300 text-gray-600 hover:border-indigo-400 hover:text-indigo-600'}
          >
            Reset
          </Button>
        </div>
      </Card>

      {/* Table */}
      <Form form={formTwo} component={false}>
        <TableData
          tableLayout={'fixed'}
          size={'middle'}
          scroll={{x: 'max-content'}}
          columns={columns}
          dataSource={dataSource}
          rowKey={'id'}
          rowClassName={'hover:bg-indigo-50 transition-colors cursor-pointer'}
          onRow={record => ({onClick: () => setViewModal({visible: true, record})})}
        />
      </Form>

      <AddProductScreen
        isModalVisible={isModalVisible}
        handleModalImageUpload={handleModalImageUpload}
        setPreviewUrls={setPreviewUrls}
        setEditModal={setEditModal}
        categoryOptions={categoryOptions}
        statusOptions={STATUS_OPTIONS}
        form={form}
        previewUrls={previewUrls}
        updateProductMutation={updateProductMutation}
        token={token}
        imageFiles={imageFiles}
        setImageFiles={setImageFiles}
        dataSource={dataSource}
        setDataSource={setDataSource}
        handleCancel={handleCancel}
        onProductCreated={product => setVariantModal({visible: true, productData: product})}
      />

      <BarcodeGenerate
        visible={barcodeModal.visible}
        product={barcodeModal.record}
        onClose={() => setBarcodeModal({visible: false, record: null})}
      />

      <EditProductScreen
        editModal={editModal}
        setEditModal={setEditModal}
        form={formTwo}
        categoryOptions={categoryOptions}
        statusOptions={STATUS_OPTIONS}
        updateProductMutation={updateProductMutation}
        token={token}
        imageFiles={imageFiles}
        setImageFiles={setImageFiles}
        dataSource={dataSource}
        setDataSource={setDataSource}
        handleModalImageUpload={handleModalImageUpload}
        handleUpdateSubmit={handleUpdateSubmit}
      />

      <ProductDetailScreen
        viewModal={viewModal}
        setViewModal={setViewModal}
        token={token}
      />
      
    </div>
  );
};

StatCard.propTypes = {
  label: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  icon:  PropTypes.node,
  color: PropTypes.string,
  bg:    PropTypes.string,
};

export default ProductScreen;
